import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// store
import { setBreadcrumbs } from '@/store/slices/breadcrumb';

// services
import { expenseRevenueService } from '@/services/expense-category.service';

// utils
import { calculateChartData, getChartConfigs } from '@/utils/chartCalculations';

// components
import { Accordion } from '@/components/ui/accordion';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import PieGraph from '@/components/organisms/graphs/PieGraph';
import { Button } from '@/components/ui/button';

// icons
import editIcon from '@/assets/icons/edit.svg';
import percentageIcon from '@/assets/icons/percentage.svg';
import euroIcon from '@/assets/icons/euro.svg';
import infoIcon from '@/assets/icons/info.svg';
import invoiceIcon from '@/assets/icons/invoice.svg';
import noInvoiceIcon from '@/assets/icons/no-invoice.svg';
import expenseIcon from '@/assets/icons/expense.svg';

import { PAGE } from '@/routes';

const ExpenseCategoryPage = () => {
  const [graphViewMode, setGraphViewMode] = useState('percentage');
  const [expenseCategories, setExpenseCategories] = useState<any[]>([]);
  const [openItems, setOpenItems] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const { budgetId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const fetchExpenseCategories = async () => {
    if (!budgetId) return;

    try {
      setLoading(true);
      const { data } = await expenseRevenueService.getExpenseCategories(budgetId);
      setExpenseCategories(data || []);
    } catch (error) {
      console.error('Error fetching expense categories:', error);
      setExpenseCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenseCategories();
  }, [budgetId]);

  useEffect(() => {
    // Set breadcrumbs when component mounts
    dispatch(
      setBreadcrumbs([
        {
          title: 'Budget',
          path: `/budgets/${budgetId}`
        },
        {
          title: 'Expense Categories Overview',
          path: ''
        }
      ])
    );
  }, [dispatch, budgetId]);

  const handleEditClick = () => {
    navigate(PAGE.EXPENSE_CATEGORY.children.ADJUST);
  };

  const handleAddClick = () => {
    navigate(PAGE.EXPENSE_CATEGORY.children.ADD);
  };

  // Mock chart data for now - you can adapt this based on your expense category data structure
  const mockChartData = [{ key: 'remaining', value: 100, fill: '#32ADE6' }];
  const { acmevChartConfig, acrtChartConfig } = getChartConfigs('secondary');

  return (
    <div className="h-full flex items-stretch justify-between gap-4">
      {/* Main - Expense Overview */}
      <div className="flex-1 flex flex-col gap-4">
        <div className="border bg-white rounded-[16px] p-4">
          <header className="flex items-start justify-between gap-4">
            <h2 className="text-2xl font-semibold uppercase">EXPENSE CATEGORIES</h2>
            <div className="flex flex-col items-end gap-3">
              <button onClick={() => console.log('Info clicked')}>
                <img className="w-[28px] h-[28px]" src={infoIcon} alt="info icon" />
              </button>

              <Tabs defaultValue="percentage" value={graphViewMode} onValueChange={setGraphViewMode}>
                <TabsList className="bg-lightPurple-200 h-auto py-[0.2rem]">
                  <TabsTrigger value="percentage" className="group md:py-[0.3rem]">
                    <img
                      src={percentageIcon}
                      alt="percentage-icon"
                      width={13}
                      height={17}
                      className="group-data-[state=active]:brightness-[100]"
                    />
                  </TabsTrigger>
                  <TabsTrigger value="euro" className="group md:py-[0.3rem]">
                    <img
                      src={euroIcon}
                      alt="euro-icon"
                      width={12}
                      height={12}
                      className="w-[12px] h-[12px] group-data-[state=active]:brightness-[100]"
                    />
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </header>

          <div className="flex items-start justify-between gap-3 mt-3">
            <PieGraph
              title="Total Expenses"
              titleValue="€0"
              chartConfig={acmevChartConfig}
              chartData={mockChartData}
              className="bg-white rounded-[16px] p-[0.85rem] flex-1 md:border border-lightGray md:py-4 md:flex md:items-center md:justify-center md:flex-col"
              outerRadius={64}
              minHeight={140}
            />

            <PieGraph
              title="Budget Utilized"
              titleValue="0%"
              chartConfig={acrtChartConfig}
              chartData={mockChartData}
              variant="primary"
              className="bg-white rounded-[16px] p-[0.85rem] flex-1 md:border border-lightGray md:py-4 md:flex md:items-center md:justify-center md:flex-col"
              outerRadius={64}
              minHeight={140}
            />
          </div>
        </div>

        <div className="flex-1 flex flex-col border bg-white rounded-[16px] p-4">
          <header>
            <h4 className="flex items-center gap-2">
              <img src={invoiceIcon} alt="invoice-icon" />
              <span className="font-bold">Recent Expenses</span>
            </h4>
          </header>
          <div className="flex-1 flex flex-col items-center justify-center gap-4">
            <div className="flex items-center gap-2">
              <img className="w-[40px] h-[40px]" src={noInvoiceIcon} alt="no-expense-icon" />
              <div className="flex flex-col items-start justify-center">
                <p className="font-medium leading-[140%]">There are no Expenses to show</p>
                <span className="text-secondary text-sm leading-[125%]">
                  Please create a new expense to track your spending!
                </span>
              </div>
            </div>
            <Button variant={'primary-reverse'} className="rounded-full px-6">
              Create an Expense
            </Button>
          </div>
        </div>
      </div>

      {/* Sidebar - All Expense Categories */}
      <div className="flex-1 min-w-[360px] max-w-[400px] border bg-white rounded-[16px] p-4 font-bold text-base overflow-y-auto">
        <header className="flex items-center justify-between gap-5">
          <span>All Expense Categories</span>

          <button onClick={handleEditClick} title="Edit">
            <img className="w-[28px] h-[28px]" src={editIcon} alt="edit icon" />
          </button>
        </header>

        <div className="flex flex-col gap-4 mt-5">
          {loading ? (
            <p className="text-sm text-secondary text-center py-4 font-normal">Loading...</p>
          ) : expenseCategories && expenseCategories.length > 0 ? (
            expenseCategories.map((category: any) => (
              <div key={category.id} className="border rounded-lg p-3">
                <div className="flex items-center gap-3">
                  <img src={expenseIcon} alt="expense-icon" className="w-6 h-6" />
                  <div className="flex-1">
                    <h5 className="font-semibold">{category.name}</h5>
                    {category.description && (
                      <p className="text-sm text-secondary font-normal">{category.description}</p>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p className="text-sm text-secondary text-center py-4 font-normal">No expense categories found</p>
          )}
        </div>
      </div>

      {/* Add Button */}
      <div className="rounded-[22px] flex-1 flex items-center justify-center absolute bottom-[26px] right-[36px] shadow-[0px_4px_4px_0px_#00000040]">
        <Button onClick={handleAddClick} className="rounded-full px-6 w-[64px] h-[64px] rounded-[22px]">
          <span className="text-white text-[2.5rem] font-light">+</span>
        </Button>
      </div>
    </div>
  );
};

export default ExpenseCategoryPage;
